import { useCallback, useEffect, useRef, useState } from "react";
import { AppShell } from "../components/AppShell";
import { InlineIcon } from "../components/InlineIcon";
import { SafeLogo } from "../components/SafeLogo";
import { Toast } from "../components/Toast";
import { C } from "../styles/tokens";
import {
  applyToJobApi,
  fetchActiveJobsApi,
  fetchAppliedJobsApi,
  fetchJobByIdApi,
  fetchMatchedJobsApi,
  fetchPreferencesApi,
  JOBS_PAGE_SIZE,
  savePreferencesApi,
} from "../api/jobs";
import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { INDUSTRY_MASTER, resolveIndustryName } from "../constants/industries";
import {
  APPLICATION_FILE_TYPES,
  NOT_GIVEN,
  appendPage,
  applicationDate,
  applicationFileProblem,
  applicationForJob,
  applicationPayload,
  emptyPreferenceCards,
  jobFacts,
  mayHaveMore,
  preferencesToEdit,
  resetPreferenceCards,
  withoutFiles,
} from "../utils/talentHub";

// Saving or applying before the saved preferences have loaded could
// overwrite them, or send an application without the CV they hold.
const PREFERENCES_NOT_LOADED =
  "Your saved job preferences have not loaded yet. Reload the page and try again.";

// Until the server answers, the cards are shown with nothing chosen.
const EMPTY_PREFERENCES = emptyPreferenceCards();

export function SearchJobsPage() {
  const isMobile = useIsMobile();
  const [applyPreferences, setApplyPreferences] = useState(() => {
    const saved = localStorage.getItem("apply_preferences_enabled");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const handleToggleApplyPreferences = (val) => {
    setApplyPreferences(val);
    localStorage.setItem("apply_preferences_enabled", JSON.stringify(val));
  };

  const { localUser } = useAuth();

  const [activeModal, setActiveModal] = useState(null); // null | 1 | 2 | 3 | 4
  const [selectedJobModal, setSelectedJobModal] = useState(null);
  const [toast, setToast] = useState(null);
  const closeToast = useCallback(() => setToast(null), []);
  const [showResetModal, setShowResetModal] = useState(false);
  const [hoverResetCancel, setHoverResetCancel] = useState(false);
  const [hoverResetConfirm, setHoverResetConfirm] = useState(false);

  // Job preferences live on the server. `savedPreferences` is what it last
  // confirmed (null until it has answered); the four cards below are being
  // edited. An edit is sent only by "Save Preferences", and closing a card
  // without saving puts the saved values back.
  const [savedPreferences, setSavedPreferences] = useState(null);
  const [preferencesError, setPreferencesError] = useState(null);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState(null);

  const [card1Data, setCard1Data] = useState(EMPTY_PREFERENCES.card1);
  const [card2Data, setCard2Data] = useState(EMPTY_PREFERENCES.card2);
  const [card3Data, setCard3Data] = useState(EMPTY_PREFERENCES.card3);
  const [card4Data, setCard4Data] = useState(EMPTY_PREFERENCES.card4);

  // Open jobs, a page at a time from the server: those that meet every
  // must-have in the saved preferences while "Apply Preferences" is on,
  // every open job otherwise.
  const [jobs, setJobs] = useState([]);
  const [jobsHasMore, setJobsHasMore] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [loadingMoreJobs, setLoadingMoreJobs] = useState(false);
  const [moreJobsError, setMoreJobsError] = useState(null);
  const [jobsReloadKey, setJobsReloadKey] = useState(0);
  // The list the held pages belong to: a page asked for an earlier list
  // (before a toggle or a save) is dropped.
  const jobsListRef = useRef(0);

  // The professional's own applications, from the server.
  const [appliedApplicants, setAppliedApplicants] = useState([]);
  const [applicationsError, setApplicationsError] = useState(null);
  const [applyingJobId, setApplyingJobId] = useState(null);

  const showPreferences = useCallback((cards) => {
    setCard1Data(cards.card1);
    setCard2Data(cards.card2);
    setCard3Data(cards.card3);
    setCard4Data(cards.card4);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPreferencesApi()
      .then((saved) => {
        if (!cancelled) setSavedPreferences(saved ?? {});
      })
      .catch((err) => {
        if (!cancelled) setPreferencesError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The saved cards, with the account's own name, email, phone and city
  // where they are empty.
  useEffect(() => {
    showPreferences(preferencesToEdit(savedPreferences, localUser));
  }, [savedPreferences, localUser, showPreferences]);

  useEffect(() => {
    let cancelled = false;
    fetchAppliedJobsApi()
      .then((list) => {
        if (!cancelled) setAppliedApplicants(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (!cancelled) setApplicationsError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchJobsPage = useCallback(
    (skip) =>
      applyPreferences
        ? fetchMatchedJobsApi(skip)
        : fetchActiveJobsApi("", "", skip),
    [applyPreferences],
  );

  // The first page, again whenever the list or the preferences change.
  useEffect(() => {
    let cancelled = false;
    jobsListRef.current += 1;
    setJobsLoading(true);
    setJobsError(null);
    setLoadingMoreJobs(false);
    setMoreJobsError(null);
    fetchJobsPage(0)
      .then((page) => {
        if (cancelled) return;
        setJobs(Array.isArray(page) ? page : []);
        setJobsHasMore(mayHaveMore(page, JOBS_PAGE_SIZE));
      })
      .catch((err) => {
        if (cancelled) return;
        setJobs([]);
        setJobsHasMore(false);
        setJobsError(err.message);
      })
      .finally(() => {
        if (!cancelled) setJobsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchJobsPage, jobsReloadKey]);

  // The next page, after the jobs held.
  const handleShowMoreJobs = async () => {
    const list = jobsListRef.current;
    setLoadingMoreJobs(true);
    setMoreJobsError(null);
    try {
      const page = await fetchJobsPage(jobs.length);
      if (list !== jobsListRef.current) return;
      setJobs((held) => appendPage(held, page));
      setJobsHasMore(mayHaveMore(page, JOBS_PAGE_SIZE));
    } catch (err) {
      if (list === jobsListRef.current) setMoreJobsError(err.message);
    } finally {
      if (list === jobsListRef.current) setLoadingMoreJobs(false);
    }
  };

  const updateCard1 = (field, val) =>
    setCard1Data((prev) => ({ ...prev, [field]: val }));
  const updateCard2 = (field, val) =>
    setCard2Data((prev) => ({ ...prev, [field]: val }));
  const updateCard3 = (field, val) =>
    setCard3Data((prev) => ({ ...prev, [field]: val }));
  const updateCard4 = (field, val) =>
    setCard4Data((prev) => ({ ...prev, [field]: val }));

  const editedPreferences = {
    card1: card1Data,
    card2: card2Data,
    card3: card3Data,
    card4: card4Data,
  };

  const openPreferenceModal = (card) => {
    setModalError(null);
    setActiveModal(card);
  };

  const closePreferenceModal = () => {
    showPreferences(preferencesToEdit(savedPreferences, localUser));
    setModalError(null);
    setActiveModal(null);
  };

  // Saves the open card; it is shown as saved only once the server has it.
  const handleSaveModal = async (cardName) => {
    if (!savedPreferences) {
      setModalError(PREFERENCES_NOT_LOADED);
      return;
    }
    setSavingPreferences(true);
    setModalError(null);
    try {
      const saved = await savePreferencesApi({
        [cardName]: editedPreferences[cardName],
      });
      setSavedPreferences(saved);
      setActiveModal(null);
      setJobsReloadKey((key) => key + 1);
      setToast({
        type: "success",
        title: "Preferences saved",
        message: "Your job preferences have been saved.",
        duration: 4000,
      });
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSavingPreferences(false);
    }
  };

  // A chosen CV or resume is read here and saved with the first card; the
  // server checks it by content. Its name is kept only together with it.
  const attachCard1File = (file, nameField, dataField) => {
    if (!file) return;
    const problem = applicationFileProblem(file);
    setModalError(problem);
    if (problem) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCard1Data((prev) => ({
        ...prev,
        [nameField]: file.name,
        [dataField]: evt.target.result,
      }));
    };
    reader.onerror = () => {
      setModalError(`${file.name} could not be read. Choose the file again.`);
    };
    reader.readAsDataURL(file);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setHoverResetCancel(false);
    setResetError(null);
  };

  const confirmResetPreferences = async () => {
    setResetting(true);
    setResetError(null);
    try {
      const saved = await savePreferencesApi(resetPreferenceCards(localUser));
      setSavedPreferences(saved);
      setShowResetModal(false);
      setJobsReloadKey((key) => key + 1);
      setToast({
        type: "success",
        title: "Preferences reset",
        message: "All four preference cards have been cleared.",
        duration: 4000,
      });
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetting(false);
    }
  };

  // Apply & Share Profile: the server keeps the application, under review,
  // with the details and files of the saved preferences.
  const handleApplyAndShareProfile = async (job) => {
    if (applyingJobId) return;
    if (!savedPreferences) {
      setToast({
        type: "error",
        title: "Application not sent",
        message: PREFERENCES_NOT_LOADED,
        duration: 0,
      });
      return;
    }
    setApplyingJobId(String(job.id));
    try {
      const created = await applyToJobApi(
        job.id,
        applicationPayload(preferencesToEdit(savedPreferences, localUser)),
      );
      setAppliedApplicants((held) => [withoutFiles(created), ...held]);
      setToast({
        type: "success",
        title: "Application sent",
        message: `Your application to ${job.company} is under review. Your TickAndPass profile has been shared with them.`,
        duration: 6000,
      });
    } catch (err) {
      setToast({
        type: "error",
        title: "Application not sent",
        message: err.message,
        duration: 0,
      });
    } finally {
      setApplyingJobId(null);
    }
  };

  // An application's job: the one held, or read from the server (it may
  // have closed since).
  const handleViewApplicationJob = async (app) => {
    const held = jobs.find((job) => String(job.id) === String(app.jobId));
    if (held) {
      setSelectedJobModal(held);
      return;
    }
    try {
      setSelectedJobModal(await fetchJobByIdApi(app.jobId));
    } catch (err) {
      setToast({
        type: "error",
        title: "Job details not available",
        message: err.message,
        duration: 0,
      });
    }
  };

  return (
    <AppShell maxWidth="100%">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 24,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            background: "#fff",
            padding: isMobile ? 16 : 24,
            borderRadius: 12,
            border: `1px solid ${C.divider}`,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 16 : 12,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: isMobile ? 20 : 24,
                fontWeight: 800,
                color: C.navy,
                margin: 0,
              }}
            >
              Talent Hub
            </h1>
            <div style={{ fontSize: 13, color: C.textMed, marginTop: 4 }}>
              Match verified credentials with top employer non-negotiable
              requirements
            </div>
          </div>
        </div>

        {/* The server's answer to an application or a save */}
        {toast && <Toast {...toast} onClose={closeToast} />}

        {/* JOB PREFERENCES SECTION (4 HORIZONTAL CARDS) */}
        <div
          style={{
            background: "#fff",
            padding: isMobile ? 16 : 24,
            borderRadius: 24,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              marginBottom: 16,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: C.navy,
                  margin: 0,
                }}
              >
                Job Preferences
              </h2>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.primary,
                  background: C.primaryLight,
                  padding: "4px 10px",
                  borderRadius: 12,
                }}
              >
                Non-Negotiable Criteria Filters
              </span>
            </div>

            {/* Controls Header Area */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: isMobile ? "space-between" : "flex-end",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {/* Reset Preferences Button */}
              <button
                onClick={() => setShowResetModal(true)}
                title="Reset all 4 job preference cards to default"
                style={{
                  padding: "6px 14px",
                  width: isMobile ? "auto" : "104px",
                  borderRadius: 8,
                  border: `1px solid ${C.red}`,
                  background: C.redLight,
                  color: C.red,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.2s ease",
                }}
              >
                Reset Preferences
              </button>

              {/* Apply Preferences Toggle */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "#f8fafc",
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: `1px solid ${C.divider}`,
                }}
              >
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.navy }}>
                    Apply Preferences
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: applyPreferences ? C.green : C.textLight,
                    }}
                  >
                    {applyPreferences
                      ? "Matching criteria active"
                      : "Showing all jobs"}
                  </div>
                </div>
                <button
                  onClick={() =>
                    handleToggleApplyPreferences(!applyPreferences)
                  }
                  title={
                    applyPreferences
                      ? "Click to show all jobs without filtering by criteria"
                      : "Click to filter jobs by your 4 non-negotiable preference cards"
                  }
                  style={{
                    width: 40,
                    height: 22,
                    borderRadius: 11,
                    border: 0,
                    cursor: "pointer",
                    position: "relative",
                    background: applyPreferences ? C.green : "#cbd5e1",
                    transition: "background 0.2s ease",
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: 3,
                      left: applyPreferences ? 21 : 3,
                      transition: "left 0.2s ease",
                    }}
                  />
                </button>
              </div>
            </div>
          </div>

          {preferencesError && (
            <p
              role="alert"
              className="m-0 mb-4 rounded-lg border border-[#B24020] bg-[#FCEBE7] px-3.5 py-2.5 text-xs font-semibold text-[#B24020]"
            >
              Your saved job preferences could not be loaded: {preferencesError}
            </p>
          )}

          <div
            data-responsive-grid="4"
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
              gap: 16,
            }}
          >
            {/* Card 1: Job Criteria */}
            <div
              onClick={() => openPreferenceModal(1)}
              style={interactiveCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 14px 30px rgba(8, 131, 62, 0.16)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "rgba(8, 131, 62, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
              }}
            >
              <h3 style={cardHeaderStyle}>1. Job Criteria</h3>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Applying:</strong> {card1Data.applyingFor || "-"}
                  <br />
                  <strong>Industry:</strong> {card1Data.industry || "-"}
                  <br />
                  <strong>Exp:</strong>{" "}
                  {card1Data.experienceYears
                    ? card1Data.experienceYears.includes("yr")
                      ? card1Data.experienceYears
                      : `${card1Data.experienceYears} yrs`
                    : "-"}
                  {card1Data.resumeFile && (
                    <div>
                      <strong>Resume:</strong> {card1Data.resumeFile}
                    </div>
                  )}
                  {card1Data.cvFile && (
                    <div>
                      <strong>CV:</strong> {card1Data.cvFile}
                    </div>
                  )}
                </div>
                <div style={cardLinkTextStyle}>Configure Criteria</div>
              </div>
            </div>

            {/* Card 2: COMPENSATION & BENEFITS */}
            <div
              onClick={() => openPreferenceModal(2)}
              style={interactiveCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 14px 30px rgba(8, 131, 62, 0.16)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "rgba(8, 131, 62, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
              }}
            >
              <h3 style={cardHeaderStyle}>2. COMPENSATION & BENEFITS</h3>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Expectation:</strong>{" "}
                  {card2Data.minSalary || card2Data.maxSalary
                    ? `${card2Data.minSalary || "0"} - ${card2Data.maxSalary || "0"}`
                    : "-"}
                  <br />
                  <strong>Notice:</strong> {card2Data.noticePeriod || "-"}
                  <br />
                  <strong>Min Stay:</strong> {card2Data.minStayMonths || "-"}
                </div>
                <div style={cardLinkTextStyle}>Configure Pay Expectation</div>
              </div>
            </div>

            {/* Card 3: WORK CONDITIONS */}
            <div
              onClick={() => openPreferenceModal(3)}
              style={interactiveCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 14px 30px rgba(8, 131, 62, 0.16)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "rgba(8, 131, 62, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
              }}
            >
              <h3 style={cardHeaderStyle}>3. WORK CONDITIONS</h3>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Model:</strong> {card3Data.workingModel || "-"}
                  <br />
                  <strong>Location:</strong>{" "}
                  {card3Data.preferredLocation || "-"}
                  <br />
                  <strong>Shift:</strong> {card3Data.preferredShift || "-"}
                </div>
                <div style={cardLinkTextStyle}>Configure Work Conditions</div>
              </div>
            </div>

            {/* Card 4: CULTURE & PERKS */}
            <div
              onClick={() => openPreferenceModal(4)}
              style={interactiveCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 14px 30px rgba(8, 131, 62, 0.16)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "rgba(8, 131, 62, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
              }}
            >
              <h3 style={cardHeaderStyle}>4. CULTURE & PERKS</h3>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Dress Code:</strong> {card4Data.dressCode || "-"}
                  <br />
                  <strong>Cab:</strong> {card4Data.cabFacilityImportant || "-"}
                  <br />
                  <strong>Cafeteria:</strong>{" "}
                  {card4Data.cafeteriaImportant || "-"}
                </div>
                <div style={cardLinkTextStyle}>Configure Perks</div>
              </div>
            </div>
          </div>
        </div>

        {/* MY APPLICATIONS TRACKER */}
        {applicationsError && (
          <p
            role="alert"
            className="m-0 rounded-lg border border-[#B24020] bg-[#FCEBE7] px-3.5 py-2.5 text-xs font-semibold text-[#B24020]"
          >
            Your applications could not be loaded: {applicationsError}
          </p>
        )}
        {appliedApplicants.length > 0 && (
          <div
            style={{
              background: "#fff",
              padding: isMobile ? 16 : 24,
              borderRadius: 24,
              border: "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                marginBottom: 16,
                gap: 10,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: C.navy,
                    margin: 0,
                  }}
                >
                  My Submitted Applications ({appliedApplicants.length})
                </h2>
                <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                  Status updates from employer review & candidate shortlisting
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {appliedApplicants.map((app) => {
                const appliedOn = applicationDate(app);

                return (
                  <div
                    key={app.id}
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      padding: isMobile ? 14 : 18,
                      borderRadius: 16,
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "stretch" : "center",
                      gap: isMobile ? 12 : 16,
                      transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 10px 24px rgba(0, 0, 0, 0.08)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 4px 16px rgba(0, 0, 0, 0.04)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <h4
                          style={{
                            fontSize: 16,
                            fontWeight: 800,
                            color: C.navy,
                            margin: 0,
                          }}
                        >
                          {app.jobTitle}
                        </h4>
                      </div>
                      <div
                        style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}
                      >
                        Company: <strong>{app.company}</strong>
                        {appliedOn && ` - Applied on ${appliedOn}`}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 10,
                        width: isMobile ? "100%" : "auto",
                      }}
                    >
                      <button
                        onClick={() => handleViewApplicationJob(app)}
                        title="View job opportunity details"
                        style={{
                          padding: "8px 14px",
                          borderRadius: 6,
                          border: `1px solid ${C.primary}`,
                          background: C.primaryLight,
                          color: C.primary,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: isMobile ? "center" : "flex-start",
                          flex: isMobile ? 1 : "initial",
                          minHeight: isMobile ? 38 : undefined,
                          gap: 6,
                        }}
                      >
                        View Job Details
                      </button>

                      {app.status === "SHORTLISTED" ? (
                        <div
                          style={{
                            padding: "8px 18px",
                            borderRadius: 20,
                            background: "#dcfce7",
                            color: "#166534",
                            fontWeight: 800,
                            fontSize: 13,
                            border: "2px solid #22c55e",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            boxShadow: "0 2px 8px rgba(34, 197, 94, 0.2)",
                          }}
                        >
                          Shortlisted by HR
                        </div>
                      ) : app.status === "REJECTED" ? (
                        <div
                          style={{
                            padding: "8px 18px",
                            borderRadius: 20,
                            background: "#fee2e2",
                            color: "#991b1b",
                            fontWeight: 800,
                            fontSize: 13,
                            border: "1px solid #fca5a5",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <InlineIcon name="close" />
                          Application Not Selected
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: "8px 18px",
                            borderRadius: 20,
                            background: C.primaryLight,
                            color: C.primary,
                            fontWeight: 700,
                            fontSize: 13,
                            border: `1px solid ${C.primary}`,
                          }}
                        >
                          Under Review
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Open jobs: those matching the preferences, or every open job */}
        <div
          style={{
            background: "#fff",
            padding: isMobile ? 16 : 24,
            borderRadius: 24,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
          }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: C.navy,
              marginBottom: 16,
            }}
          >
            {applyPreferences
              ? "Matching Opportunities"
              : "All Available Opportunities"}{" "}
            ({jobs.length}
            {jobsHasMore ? "+" : ""})
          </h2>

          {jobsLoading ? (
            <p className="m-0 p-5 text-center text-[13px] text-[#4E5D78]">
              Loading opportunities...
            </p>
          ) : jobsError ? (
            <div
              role="alert"
              className="flex flex-col items-center gap-3 p-5 text-center text-[13px] text-[#B24020]"
            >
              <span>Opportunities could not be loaded: {jobsError}</span>
              <button
                type="button"
                onClick={() => setJobsReloadKey((key) => key + 1)}
                className="cursor-pointer rounded-md border border-[#08833E] bg-[#E7F4EC] px-4 py-2 text-[13px] font-bold text-[#08833E]"
              >
                Try again
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: C.textMed,
                fontSize: 13,
              }}
            >
              {applyPreferences
                ? "No open opportunities meet all of your non-negotiable criteria. Turn off 'Apply Preferences' to see every open job, or update your criteria."
                : "No active job opportunities currently available."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {jobs.map((job) => {
                const appRecord = applicationForJob(appliedApplicants, job.id);
                const status = appRecord?.status;
                const isApplied = Boolean(appRecord);
                const facts = jobFacts(job);

                return (
                  <div
                    key={job.id}
                    style={{
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      padding: isMobile ? 16 : 22,
                      borderRadius: 18,
                      background: "#ffffff",
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.04)",
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "stretch" : "center",
                      gap: isMobile ? 14 : 16,
                      transition:
                        "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 12px 28px rgba(8, 131, 62, 0.12)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.borderColor =
                        "rgba(8, 131, 62, 0.35)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(0, 0, 0, 0.04)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: 18,
                            fontWeight: 800,
                            color: C.navy,
                            margin: 0,
                          }}
                        >
                          {job.title}
                        </h3>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            background: C.blueLight,
                            color: C.blue,
                            padding: "2px 8px",
                            borderRadius: 10,
                          }}
                        >
                          Active Opportunity
                        </span>
                        {status === "SHORTLISTED" && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 800,
                              background: "#dcfce7",
                              color: "#166534",
                              padding: "3px 10px",
                              borderRadius: 12,
                              border: "1.5px solid #22c55e",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            Shortlisted by HR
                          </span>
                        )}
                        {status === "REJECTED" && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 800,
                              background: "#fee2e2",
                              color: "#991b1b",
                              padding: "3px 10px",
                              borderRadius: 12,
                              border: "1.5px solid #ef4444",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <InlineIcon name="close" size={12} />
                            Rejected by HR
                          </span>
                        )}
                        {(status === "UNDER_REVIEW" ||
                          (isApplied && !status)) && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              background: "#fef3c7",
                              color: "#92400e",
                              padding: "3px 10px",
                              borderRadius: 12,
                              border: "1px solid #fde68a",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            Under Review
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginTop: 6,
                        }}
                      >
                        <SafeLogo
                          src={job.organisationLogoUrl}
                          alt={job.company || "Company"}
                          fallbackText={
                            job.company
                              ? job.company.slice(0, 2).toUpperCase()
                              : "CO"
                          }
                          imgStyle={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            objectFit: "contain",
                            border: "1px solid #e2e8f0",
                            background: "#ffffff",
                            padding: 2,
                            flexShrink: 0,
                          }}
                          fallbackStyle={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#64748b",
                            fontSize: 11,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.textDark,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            {job.company}
                            {job.verifiedOrg && (
                              <span style={{ color: C.primary, fontSize: 12 }}>
                                <InlineIcon
                                  name="check"
                                  size={12}
                                  className="mr-1"
                                />
                                Verified Employer
                              </span>
                            )}
                          </div>
                          {job.organisationDescription && (
                            <div
                              style={{
                                fontSize: 11.5,
                                color: "#64748b",
                                marginTop: 1,
                              }}
                            >
                              {job.organisationDescription}
                            </div>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 16,
                          fontSize: 12,
                          color: C.textDark,
                          marginTop: 10,
                          background: "#fff",
                          padding: "8px 14px",
                          borderRadius: 6,
                          border: `1px solid ${C.divider}`,
                        }}
                      >
                        <span>
                          <strong>Role / Position:</strong> {job.title}
                        </span>
                        <span>
                          <strong>Location:</strong>{" "}
                          {facts.location ?? NOT_GIVEN}
                        </span>
                        <span>
                          <strong>Experience:</strong>{" "}
                          {facts.experience ?? NOT_GIVEN}
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 10,
                        alignItems: isMobile ? "stretch" : "center",
                        width: isMobile ? "100%" : "auto",
                        whiteSpace: isMobile ? "normal" : "nowrap",
                      }}
                    >
                      <button
                        onClick={() => setSelectedJobModal(job)}
                        style={{
                          padding: "10px 18px",
                          borderRadius: 6,
                          border: `1px solid ${C.primary}`,
                          background: C.primaryLight,
                          color: C.primary,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontSize: 13,
                          width: isMobile ? "100%" : "auto",
                          minHeight: isMobile ? 44 : undefined,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        View Job
                      </button>

                      {status === "SHORTLISTED" ? (
                        <div
                          style={{
                            padding: "8px 18px",
                            borderRadius: 20,
                            background: "#dcfce7",
                            color: "#166534",
                            fontWeight: 800,
                            fontSize: 13,
                            border: "2px solid #22c55e",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            boxShadow: "0 2px 8px rgba(34, 197, 94, 0.2)",
                            width: isMobile ? "100%" : "auto",
                            minHeight: isMobile ? 44 : undefined,
                          }}
                        >
                          Shortlisted by HR
                        </div>
                      ) : status === "REJECTED" ? (
                        <div
                          style={{
                            padding: "8px 18px",
                            borderRadius: 20,
                            background: "#fee2e2",
                            color: "#991b1b",
                            fontWeight: 800,
                            fontSize: 13,
                            border: "2px solid #ef4444",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            width: isMobile ? "100%" : "auto",
                            minHeight: isMobile ? 44 : undefined,
                          }}
                        >
                          <InlineIcon name="close" />
                          Application Rejected by HR
                        </div>
                      ) : isApplied ? (
                        <button
                          disabled
                          title="You have already applied and shared your verified profile for this opportunity"
                          style={{
                            padding: "10px 20px",
                            borderRadius: 6,
                            border: `1px solid ${C.divider}`,
                            background: "#e2e8f0",
                            color: "#64748b",
                            fontWeight: 700,
                            cursor: "not-allowed",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            width: isMobile ? "100%" : "auto",
                            minHeight: isMobile ? 44 : undefined,
                          }}
                        >
                          <InlineIcon name="check" />
                          Profile Shared & Applied
                        </button>
                      ) : (
                        <button
                          onClick={() => handleApplyAndShareProfile(job)}
                          disabled={applyingJobId !== null}
                          aria-busy={applyingJobId === String(job.id)}
                          className="disabled:opacity-60"
                          style={{
                            padding: "10px 20px",
                            borderRadius: 6,
                            border: 0,
                            background: C.primary,
                            color: "#fff",
                            fontWeight: 700,
                            cursor: "pointer",
                            width: isMobile ? "100%" : "auto",
                            minHeight: isMobile ? 44 : undefined,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          Apply & Share Profile
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {jobsHasMore && (
                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={handleShowMoreJobs}
                    disabled={loadingMoreJobs}
                    className="min-h-[44px] w-full cursor-pointer rounded-md border border-[#08833E] bg-[#E7F4EC] px-[18px] py-2.5 text-[13px] font-bold text-[#08833E] disabled:cursor-not-allowed disabled:opacity-60 min-[700px]:w-auto"
                  >
                    {loadingMoreJobs ? "Loading more..." : "Show more"}
                  </button>
                  {moreJobsError && (
                    <p
                      role="alert"
                      className="m-0 text-center text-xs font-semibold text-[#B24020]"
                    >
                      More opportunities could not be loaded: {moreJobsError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MODAL 1: JOB CRITERIA */}
      {activeModal === 1 && (
        <ModalWrapper
          title="1. Job Criteria Preferences"
          onClose={closePreferenceModal}
          onSave={() => handleSaveModal("card1")}
          saving={savingPreferences}
          error={modalError}
        >
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              padding: "10px 14px",
              borderRadius: 8,
              color: "#166534",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <InlineIcon name="info" />
            Your name, email, phone and city are filled in from your account
            where it has them. Check them before saving.
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <label style={labelStyle}>
              <span>Applying for</span>
              <input
                type="text"
                value={card1Data.applyingFor}
                onChange={(e) => updateCard1("applyingFor", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Name</span>
              <input
                type="text"
                value={card1Data.name}
                onChange={(e) => updateCard1("name", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>DOB</span>
              <input
                type="date"
                value={card1Data.dob}
                onChange={(e) => updateCard1("dob", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Gender</span>
              <select
                value={card1Data.gender}
                onChange={(e) => updateCard1("gender", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Any">Any</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Languages Known</span>
              <input
                type="text"
                value={card1Data.languagesKnown}
                onChange={(e) => updateCard1("languagesKnown", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Level of Education</span>
              <select
                value={card1Data.educationLevel}
                onChange={(e) => updateCard1("educationLevel", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="High School">High School</option>
                <option value="Higher Secondary">Higher Secondary</option>
                <option value="Diploma">Diploma</option>
                <option value="UG">UG</option>
                <option value="PG">PG</option>
                <option value="PhD">PhD</option>
                <option value="Post Doctoral">Post Doctoral</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Required Degree</span>
              <input
                type="text"
                value={card1Data.requiredDegree}
                onChange={(e) => updateCard1("requiredDegree", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Educational Institution</span>
              <input
                type="text"
                value={card1Data.educationalInstitution}
                onChange={(e) =>
                  updateCard1("educationalInstitution", e.target.value)
                }
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Certifications / Licences</span>
              <input
                type="text"
                value={card1Data.certifications}
                onChange={(e) => updateCard1("certifications", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Military Veteran?</span>
              <select
                value={card1Data.militaryVeteran}
                onChange={(e) => updateCard1("militaryVeteran", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            {/* Preferred Industries Section with Non-Negotiable Toggle */}
            <div
              style={{
                gridColumn: "span 2",
                background: "#f8fafc",
                padding: 14,
                borderRadius: 8,
                border: `1px solid ${C.divider}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 800, color: C.navy }}>
                  Preferred Industries (Industry Master)
                </span>

                {/* Non-Negotiable Toggle for Industry */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.textDark,
                  }}
                >
                  <span>Non-Negotiable:</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      updateCard1(
                        "industryNonNegotiable",
                        !card1Data.industryNonNegotiable,
                      );
                    }}
                    style={{
                      width: 44,
                      height: 22,
                      borderRadius: 11,
                      border: 0,
                      cursor: "pointer",
                      position: "relative",
                      background: card1Data.industryNonNegotiable
                        ? C.red
                        : "#cbd5e1",
                      transition: "background 0.2s ease",
                      padding: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#fff",
                        position: "absolute",
                        top: 3,
                        left: card1Data.industryNonNegotiable ? 25 : 3,
                        transition: "left 0.2s ease",
                      }}
                    />
                  </button>
                  <span
                    style={{
                      color: card1Data.industryNonNegotiable
                        ? C.red
                        : C.textLight,
                      fontWeight: 800,
                    }}
                  >
                    {card1Data.industryNonNegotiable ? "ON" : "OFF"}
                  </span>
                </label>
              </div>

              {/* Selected Industry Chips */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                {(card1Data.preferredIndustryIds || []).map((id) => {
                  const indName = resolveIndustryName(id);
                  return (
                    <span
                      key={id}
                      style={{
                        background: C.primaryLight,
                        color: C.primary,
                        padding: "4px 12px",
                        borderRadius: 16,
                        fontSize: 12,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        border: `1px solid ${C.primary}`,
                      }}
                    >
                      {indName}
                      <button
                        type="button"
                        aria-label={`Remove ${indName}`}
                        onClick={() => {
                          const updated = card1Data.preferredIndustryIds.filter(
                            (item) => item !== id,
                          );
                          updateCard1("preferredIndustryIds", updated);
                          // With none left, no industry stays chosen (the
                          // server would still match on the last one).
                          updateCard1("industryId", updated[0] ?? "");
                          updateCard1(
                            "industry",
                            updated.length > 0
                              ? resolveIndustryName(updated[0])
                              : "",
                          );
                        }}
                        style={{
                          border: 0,
                          background: "transparent",
                          color: C.primary,
                          fontWeight: 800,
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        <InlineIcon name="close" size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>

              {/* Select dropdown to add industry */}
              <select
                onChange={(e) => {
                  const val = e.target.value;
                  if (
                    val &&
                    !(card1Data.preferredIndustryIds || []).includes(val)
                  ) {
                    const updated = [
                      ...(card1Data.preferredIndustryIds || []),
                      val,
                    ];
                    updateCard1("preferredIndustryIds", updated);
                    updateCard1("industryId", val);
                    updateCard1("industry", resolveIndustryName(val));
                  }
                }}
                value=""
                style={inputStyle}
              >
                <option value="">
                  + Add Preferred Industry from Centralized Master List...
                </option>
                {INDUSTRY_MASTER.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </select>
            </div>

            <label style={labelStyle}>
              <span>Department / Specialisation</span>
              <input
                type="text"
                value={card1Data.department}
                onChange={(e) => updateCard1("department", e.target.value)}
                style={inputStyle}
              />
            </label>

            {/* Experience with Non-Negotiable Toggle */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: C.textDark }}
                >
                  Years of Experience
                </span>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  <span>Non-Negotiable:</span>
                  <input
                    type="checkbox"
                    checked={Boolean(card1Data.experienceNonNegotiable)}
                    onChange={(e) =>
                      updateCard1("experienceNonNegotiable", e.target.checked)
                    }
                  />
                </label>
              </div>
              <select
                value={card1Data.experienceYears}
                onChange={(e) => updateCard1("experienceYears", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Experience</option>
                {EXP_YEARS_OPTIONS.map((exp) => (
                  <option key={exp} value={exp}>
                    {exp}
                  </option>
                ))}
              </select>
            </div>

            {/* Employment Type with Non-Negotiable Toggle */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: C.textDark }}
                >
                  Employment Type
                </span>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  <span>Non-Negotiable:</span>
                  <input
                    type="checkbox"
                    checked={Boolean(card1Data.employmentTypeNonNegotiable)}
                    onChange={(e) =>
                      updateCard1(
                        "employmentTypeNonNegotiable",
                        e.target.checked,
                      )
                    }
                  />
                </label>
              </div>
              <select
                value={card1Data.employmentType}
                onChange={(e) => updateCard1("employmentType", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
                <option value="Freelance">Freelance</option>
              </select>
            </div>
            <label style={labelStyle}>
              <span>
                Willing to be considered for multiple similar openings?
              </span>
              <select
                value={card1Data.multipleOpenings}
                onChange={(e) =>
                  updateCard1("multipleOpenings", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Contact Number</span>
              <input
                type="text"
                value={card1Data.contactNumber}
                onChange={(e) => updateCard1("contactNumber", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Email</span>
              <input
                type="email"
                value={card1Data.email}
                onChange={(e) => updateCard1("email", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ ...labelStyle, gridColumn: "span 2" }}>
              <span>Skillset / Expertise</span>
              <textarea
                value={card1Data.skillset}
                onChange={(e) => updateCard1("skillset", e.target.value)}
                style={textareaStyle}
                rows={2}
              />
            </label>
            <label style={{ ...labelStyle, gridColumn: "span 2" }}>
              <span>Postal Address</span>
              <input
                type="text"
                value={card1Data.address}
                onChange={(e) => updateCard1("address", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ ...labelStyle, gridColumn: "span 1" }}>
              <span>
                Upload Resume (PDF, Word, Excel, JPG or PNG, up to 5 MB)
              </span>
              <input
                type="file"
                accept={APPLICATION_FILE_TYPES}
                onChange={(e) =>
                  attachCard1File(
                    e.target.files?.[0],
                    "resumeFile",
                    "resumeDataUrl",
                  )
                }
                style={{ ...inputStyle, padding: "6px" }}
              />
              {card1Data.resumeFile && (
                <span
                  style={{
                    fontSize: 11,
                    color: C.green,
                    fontWeight: 700,
                    marginTop: 2,
                  }}
                >
                  <InlineIcon name="check" size={12} className="mr-1" />
                  Selected: {card1Data.resumeFile}
                </span>
              )}
            </label>
            <label style={{ ...labelStyle, gridColumn: "span 1" }}>
              <span>Upload CV (PDF, Word, Excel, JPG or PNG, up to 5 MB)</span>
              <input
                type="file"
                accept={APPLICATION_FILE_TYPES}
                onChange={(e) =>
                  attachCard1File(e.target.files?.[0], "cvFile", "cvDataUrl")
                }
                style={{ ...inputStyle, padding: "6px" }}
              />
              {card1Data.cvFile && (
                <span
                  style={{
                    fontSize: 11,
                    color: C.green,
                    fontWeight: 700,
                    marginTop: 2,
                  }}
                >
                  <InlineIcon name="check" size={12} className="mr-1" />
                  Selected: {card1Data.cvFile}
                </span>
              )}
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* MODAL 2: COMPENSATION & BENEFITS */}
      {activeModal === 2 && (
        <ModalWrapper
          title="2. Compensation & Benefits Preferences"
          onClose={closePreferenceModal}
          onSave={() => handleSaveModal("card2")}
          saving={savingPreferences}
          error={modalError}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <label style={labelStyle}>
              <span>Minimum Salary Expectation</span>
              <input
                type="text"
                value={card2Data.minSalary}
                onChange={(e) => updateCard2("minSalary", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Maximum Salary Expectation</span>
              <input
                type="text"
                value={card2Data.maxSalary}
                onChange={(e) => updateCard2("maxSalary", e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Joining bonus important to you?</span>
              <select
                value={card2Data.joiningBonusImportant}
                onChange={(e) =>
                  updateCard2("joiningBonusImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Open to variable / performance-linked pay?</span>
              <select
                value={card2Data.variablePayOpen}
                onChange={(e) => updateCard2("variablePayOpen", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Health insurance important to you?</span>
              <select
                value={card2Data.healthInsuranceImportant}
                onChange={(e) =>
                  updateCard2("healthInsuranceImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Statutory benefits (PF / ESI) important?</span>
              <select
                value={card2Data.statutoryBenefitsImportant}
                onChange={(e) =>
                  updateCard2("statutoryBenefitsImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Value equity / ESOPs as part of compensation?</span>
              <select
                value={card2Data.valueESOPs}
                onChange={(e) => updateCard2("valueESOPs", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Value learning & development support?</span>
              <select
                value={card2Data.valueLD}
                onChange={(e) => updateCard2("valueLD", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: C.textDark }}
                >
                  Notice period to join
                </span>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  <span>Non-Negotiable:</span>
                  <input
                    type="checkbox"
                    checked={Boolean(card2Data.noticePeriodNonNegotiable)}
                    onChange={(e) =>
                      updateCard2("noticePeriodNonNegotiable", e.target.checked)
                    }
                  />
                </label>
              </div>
              <select
                value={card2Data.noticePeriod}
                onChange={(e) => updateCard2("noticePeriod", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="immediate">Immediate</option>
                <option value="15 days">15 days</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
              </select>
            </div>
            <label style={labelStyle}>
              <span>Minimum stay commitment</span>
              <select
                value={card2Data.minStayMonths}
                onChange={(e) => updateCard2("minStayMonths", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="0-6 months">0-6 months</option>
                <option value="1 year">1 year</option>
                <option value="2 years">2 years</option>
                <option value="2-5 years">2-5 years</option>
                <option value="cannot say">Cannot say</option>
              </select>
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* MODAL 3: WORK CONDITIONS */}
      {activeModal === 3 && (
        <ModalWrapper
          title="3. Work Conditions Preferences"
          onClose={closePreferenceModal}
          onSave={() => handleSaveModal("card3")}
          saving={savingPreferences}
          error={modalError}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <label style={labelStyle}>
              <span>Preferred Working Model</span>
              <select
                value={card3Data.workingModel}
                onChange={(e) => updateCard3("workingModel", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Office">Office</option>
                <option value="work from home">Work from home</option>
                <option value="Hybrid">Hybrid</option>
                <option value="any">Any</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Current Location</span>
              <input
                type="text"
                value={card3Data.currentLocation}
                onChange={(e) => updateCard3("currentLocation", e.target.value)}
                style={inputStyle}
              />
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: C.textDark }}
                >
                  Preferred Location
                </span>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  <span>Non-Negotiable:</span>
                  <input
                    type="checkbox"
                    checked={Boolean(card3Data.locationNonNegotiable)}
                    onChange={(e) =>
                      updateCard3("locationNonNegotiable", e.target.checked)
                    }
                  />
                </label>
              </div>
              <select
                value={card3Data.preferredLocation}
                onChange={(e) =>
                  updateCard3("preferredLocation", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                {DISTRICTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <label style={labelStyle}>
              <span>Willing to relocate?</span>
              <select
                value={card3Data.willingToRelocate}
                onChange={(e) =>
                  updateCard3("willingToRelocate", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Willing to travel locally?</span>
              <select
                value={card3Data.willingLocalTravel}
                onChange={(e) =>
                  updateCard3("willingLocalTravel", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Willing to travel within country?</span>
              <select
                value={card3Data.willingCountryTravel}
                onChange={(e) =>
                  updateCard3("willingCountryTravel", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Willing to travel internationally?</span>
              <select
                value={card3Data.willingIntlTravel}
                onChange={(e) =>
                  updateCard3("willingIntlTravel", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Preferred Work Shift</span>
              <select
                value={card3Data.preferredShift}
                onChange={(e) => updateCard3("preferredShift", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Day">Day</option>
                <option value="evening">Evening</option>
                <option value="night">Night</option>
                <option value="any">Any</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Max work hours per day</span>
              <select
                value={card3Data.maxHoursPerDay}
                onChange={(e) => updateCard3("maxHoursPerDay", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="less than 4 hours">Less than 4 hours</option>
                <option value="4-8 hours">4-8 hours</option>
                <option value="8-9">8-9 hours</option>
                <option value="9-10">9-10 hours</option>
                <option value="more than 10">More than 10 hours</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Flexible with work hours?</span>
              <select
                value={card3Data.flexibleWorkHours}
                onChange={(e) =>
                  updateCard3("flexibleWorkHours", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Can extend beyond work hours?</span>
              <select
                value={card3Data.extendBeyondShift}
                onChange={(e) =>
                  updateCard3("extendBeyondShift", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Willing to work weekends?</span>
              <select
                value={card3Data.willingWeekends}
                onChange={(e) => updateCard3("willingWeekends", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Preferred team size / structure</span>
              <input
                type="text"
                value={card3Data.teamSizeReporting}
                onChange={(e) =>
                  updateCard3("teamSizeReporting", e.target.value)
                }
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Comfortable with probation period?</span>
              <select
                value={card3Data.probationPeriod}
                onChange={(e) => updateCard3("probationPeriod", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Negotiable">Negotiable</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Preferred Interview Mode</span>
              <select
                value={card3Data.interviewMode}
                onChange={(e) => updateCard3("interviewMode", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Virtual">Virtual</option>
                <option value="In-person">In-person</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Background Verification</span>
              <select
                value={card3Data.backgroundVerification}
                onChange={(e) =>
                  updateCard3("backgroundVerification", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Need visa / relocation sponsorship?</span>
              <select
                value={card3Data.visaSponsorship}
                onChange={(e) => updateCard3("visaSponsorship", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Need company laptop / equipment?</span>
              <select
                value={card3Data.laptopProvided}
                onChange={(e) => updateCard3("laptopProvided", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* MODAL 4: CULTURE & PERKS */}
      {activeModal === 4 && (
        <ModalWrapper
          title="4. Culture & Perks Preferences"
          onClose={closePreferenceModal}
          onSave={() => handleSaveModal("card4")}
          saving={savingPreferences}
          error={modalError}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <label style={labelStyle}>
              <span>Cab facility important to you?</span>
              <select
                value={card4Data.cabFacilityImportant}
                onChange={(e) =>
                  updateCard4("cabFacilityImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Cafeteria facility important?</span>
              <select
                value={card4Data.cafeteriaImportant}
                onChange={(e) =>
                  updateCard4("cafeteriaImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Leave policy (CL/SL/EL) important?</span>
              <select
                value={card4Data.leavePolicyImportant}
                onChange={(e) =>
                  updateCard4("leavePolicyImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Safety provisions important?</span>
              <select
                value={card4Data.safetyProvisionsImportant}
                onChange={(e) =>
                  updateCard4("safetyProvisionsImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Value mental health / wellness support?</span>
              <select
                value={card4Data.mentalHealthImportant}
                onChange={(e) =>
                  updateCard4("mentalHealthImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>D&I policies important to you?</span>
              <select
                value={card4Data.diPoliciesImportant}
                onChange={(e) =>
                  updateCard4("diPoliciesImportant", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span>Preferred dress code</span>
              <select
                value={card4Data.dressCode}
                onChange={(e) => updateCard4("dressCode", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Business casual">Business casual</option>
                <option value="Formal">Formal</option>
                <option value="Casual">Casual</option>
                <option value="Any">Any</option>
              </select>
            </label>
            <label style={{ ...labelStyle, gridColumn: "span 2" }}>
              <span>Preferred team culture / work style</span>
              <input
                type="text"
                value={card4Data.teamCultureStyle}
                onChange={(e) =>
                  updateCard4("teamCultureStyle", e.target.value)
                }
                style={inputStyle}
              />
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* JOB DETAILS MODAL (VIEW JOB) */}
      {selectedJobModal && (
        <JobDetailsModal
          job={selectedJobModal}
          appliedApplicants={appliedApplicants}
          onClose={() => setSelectedJobModal(null)}
          applying={applyingJobId !== null}
          onApply={handleApplyAndShareProfile}
        />
      )}

      {/* RESET PREFERENCES CONFIRMATION MODAL */}
      {showResetModal && (
        <div style={modalOverlayStyle}>
          <div
            style={{
              ...modalContentStyle,
              maxWidth: 460,
              textAlign: "center",
              padding: isMobile ? 16 : 28,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <img
                src="/reset-green.png"
                alt="Reset All Job Preferences"
                style={{
                  width: 60,
                  height: 60,
                  objectFit: "contain",
                  display: "block",
                  filter: "drop-shadow(0 4px 10px rgba(8, 131, 62, 0.2))",
                }}
              />
            </div>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: C.navy,
                margin: "0 0 10px",
              }}
            >
              Reset All Job Preferences?
            </h2>
            <p
              style={{
                fontSize: 13,
                color: C.textDark,
                lineHeight: 1.6,
                marginBottom: 24,
              }}
            >
              Are you sure you want to reset all 4 preference categories (
              <strong>Job Criteria</strong>, <strong>Compensation</strong>,{" "}
              <strong>Work Conditions</strong> & <strong>Perks</strong>)? This
              will clear your custom filter criteria, including the resume and
              CV you attached, and update job recommendations.
            </p>
            {resetError && (
              <p
                role="alert"
                className="m-0 mb-4 rounded-lg border border-[#B24020] bg-[#FCEBE7] px-3.5 py-2.5 text-left text-xs font-semibold text-[#B24020]"
              >
                Your preferences were not reset: {resetError}
              </p>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 12,
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={closeResetModal}
                onMouseEnter={() => setHoverResetCancel(true)}
                onMouseLeave={() => setHoverResetCancel(false)}
                style={{
                  padding: "10px 22px",
                  borderRadius: 8,
                  border: `1px solid ${hoverResetCancel ? C.primary : C.dividerStrong || "#cbd5e1"}`,
                  background: hoverResetCancel ? C.primary : "#fff",
                  color: hoverResetCancel ? "#fff" : C.textDark,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  flex: 1,
                  minHeight: 44,
                  transition: "all 0.2s ease",
                  boxShadow: hoverResetCancel
                    ? "0 4px 14px rgba(8, 131, 62, 0.3)"
                    : "none",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setHoverResetConfirm(false);
                  confirmResetPreferences();
                }}
                disabled={resetting}
                className="disabled:opacity-60"
                onMouseEnter={() => setHoverResetConfirm(true)}
                onMouseLeave={() => setHoverResetConfirm(false)}
                style={{
                  padding: "10px 22px",
                  borderRadius: 8,
                  border: `1px solid ${hoverResetConfirm ? "#b91c1c" : "#dc2626"}`,
                  background: hoverResetConfirm ? "#b91c1c" : "#dc2626",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  flex: 1,
                  minHeight: 44,
                  boxShadow: hoverResetConfirm
                    ? "0 6px 16px rgba(220, 38, 38, 0.4)"
                    : "0 4px 12px rgba(220, 38, 38, 0.25)",
                  transition: "all 0.2s ease",
                }}
              >
                {resetting ? "Resetting..." : "Yes, Reset All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

// Job Details Modal Component
// `applying` while an application is being sent (one at a time).
function JobDetailsModal({
  job,
  appliedApplicants = [],
  applying,
  onClose,
  onApply,
}) {
  const isMobile = useIsMobile();
  if (!job) return null;
  const c1 = job.card1 || {};
  const c2 = job.card2 || {};
  const c3 = job.card3 || {};
  const c4 = job.card4 || {};
  const facts = jobFacts(job);

  const appRecord = applicationForJob(appliedApplicants, job.id);
  const status = appRecord?.status;
  const isApplied = Boolean(appRecord);

  return (
    <div style={{ ...modalOverlayStyle, padding: isMobile ? 12 : 20 }}>
      <div
        style={{
          ...modalContentStyle,
          maxWidth: 750,
          maxHeight: "88vh",
          overflowY: "auto",
          padding: isMobile ? 16 : 28,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: `1px solid ${C.divider}`,
            paddingBottom: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: C.navy,
                  margin: 0,
                }}
              >
                {job.title}
              </h2>
              {status === "SHORTLISTED" && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    background: "#dcfce7",
                    color: "#166534",
                    padding: "3px 10px",
                    borderRadius: 12,
                    border: "1.5px solid #22c55e",
                  }}
                >
                  Shortlisted by HR
                </span>
              )}
              {status === "REJECTED" && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    background: "#fee2e2",
                    color: "#991b1b",
                    padding: "3px 10px",
                    borderRadius: 12,
                    border: "1.5px solid #ef4444",
                  }}
                >
                  <InlineIcon name="close" size={12} className="mr-1" />
                  Rejected by HR
                </span>
              )}
              {(status === "UNDER_REVIEW" || (isApplied && !status)) && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    background: "#fef3c7",
                    color: "#92400e",
                    padding: "3px 10px",
                    borderRadius: 12,
                    border: "1px solid #fde68a",
                  }}
                >
                  Under Review
                </span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 6,
              }}
            >
              <SafeLogo
                src={job.organisationLogoUrl}
                alt={job.company || "Company"}
                fallbackText={
                  job.company ? job.company.slice(0, 2).toUpperCase() : "CO"
                }
                imgStyle={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  objectFit: "contain",
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                  padding: 2,
                  flexShrink: 0,
                }}
                fallbackStyle={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: C.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {job.company}
                  {job.verifiedOrg && (
                    <span className="inline-flex items-center gap-1">
                      <InlineIcon name="check" />
                      Verified Employer
                    </span>
                  )}
                </div>
                {job.organisationDescription && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      marginTop: 2,
                    }}
                  >
                    {job.organisationDescription}
                  </div>
                )}
              </div>
            </div>
            {(job.postedAt || (job.fromDate && job.toDate)) && (
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                {[
                  job.postedAt && `Posted on ${job.postedAt}`,
                  job.fromDate &&
                    job.toDate &&
                    `Application window: ${job.fromDate} to ${job.toDate}`,
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 24,
              cursor: "pointer",
              color: C.textMed,
            }}
          >
            <InlineIcon name="close" size={20} />
          </button>
        </div>

        {/* Full Job Description */}
        <div style={{ marginBottom: 20 }}>
          <h3
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: C.navy,
              marginBottom: 8,
            }}
          >
            Job Description
          </h3>
          <div
            style={{
              fontSize: 13,
              color: C.textDark,
              lineHeight: 1.6,
              background: "#f8fafc",
              padding: 16,
              borderRadius: 8,
              border: `1px solid ${C.divider}`,
              whiteSpace: "pre-wrap",
            }}
          >
            {job.jobDescription || "No detailed job description provided."}
          </div>
        </div>

        {/* Structured Specs Grid */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            fontSize: 13,
          }}
        >
          {/* 1. Requirements */}
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            <h4
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: C.primary,
                margin: "0 0 10px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 6,
              }}
            >
              1. Job Requirements
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 10,
              }}
            >
              <div>
                <strong>Opening For:</strong> {c1.openingFor || job.title}
              </div>
              <div>
                <strong>Required Degree:</strong>{" "}
                {facts.requiredDegree ?? NOT_GIVEN}
              </div>
              <div>
                <strong>Experience:</strong> {facts.experience ?? NOT_GIVEN}
              </div>
              <div>
                <strong>Essential Skillset:</strong>{" "}
                {facts.essentialSkillset ?? NOT_GIVEN}
              </div>
              {c1.educationLevel && (
                <div>
                  <strong>Education Level:</strong> {c1.educationLevel}
                </div>
              )}
              {c1.educationalInstitution && (
                <div>
                  <strong>Institution:</strong> {c1.educationalInstitution}
                </div>
              )}
              {c1.certificationsRequired && (
                <div>
                  <strong>Certifications:</strong> {c1.certificationsRequired}
                </div>
              )}
              {c1.employmentType && (
                <div>
                  <strong>Employment Type:</strong> {c1.employmentType}
                </div>
              )}
              {c1.openPositions && (
                <div>
                  <strong>Positions:</strong> {c1.openPositions}
                </div>
              )}
            </div>
          </div>

          {/* 2. Compensation & Benefits */}
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            <h4
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: C.primary,
                margin: "0 0 10px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 6,
              }}
            >
              2. Compensation & Benefits
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 10,
              }}
            >
              <div>
                <strong>Salary:</strong> {facts.salary ?? NOT_GIVEN}
              </div>
              <div>
                <strong>Notice Period:</strong>{" "}
                {facts.noticePeriod ?? NOT_GIVEN}
              </div>
              {c2.statutoryBenefits && (
                <div>
                  <strong>Statutory Benefits:</strong> {c2.statutoryBenefits}
                </div>
              )}
              {c2.joiningBonus && (
                <div>
                  <strong>Joining Bonus:</strong> {c2.joiningBonus}
                </div>
              )}
              {c2.performanceBonus && (
                <div>
                  <strong>Performance Bonus:</strong> {c2.performanceBonus}
                </div>
              )}
              {c2.healthInsurance && (
                <div>
                  <strong>Health Insurance:</strong> {c2.healthInsurance}
                </div>
              )}
              {c2.esopsOffered && (
                <div>
                  <strong>ESOPs Offered:</strong> {c2.esopsOffered}
                </div>
              )}
            </div>
          </div>

          {/* 3. Work Conditions */}
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            <h4
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: C.primary,
                margin: "0 0 10px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 6,
              }}
            >
              3. Work Conditions
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 10,
              }}
            >
              <div>
                <strong>Location:</strong> {facts.location ?? NOT_GIVEN}
              </div>
              <div>
                <strong>Working Model:</strong> {facts.workModel ?? NOT_GIVEN}
              </div>
              {c3.workShift && (
                <div>
                  <strong>Work Shift:</strong> {c3.workShift}
                </div>
              )}
              {c3.maxHoursPerDay && (
                <div>
                  <strong>Max Hours / Day:</strong> {c3.maxHoursPerDay}
                </div>
              )}
              {c3.probationMonths && (
                <div>
                  <strong>Probation Period:</strong> {c3.probationMonths} months
                </div>
              )}
              {c3.interviewMode && (
                <div>
                  <strong>Interview Mode:</strong> {c3.interviewMode}
                </div>
              )}
            </div>
          </div>

          {/* 4. Culture & Perks */}
          {(c4.dressCode ||
            c4.cabService ||
            c4.foodProvided ||
            c4.teamCulture) && (
            <div
              style={{
                background: "#fff",
                border: `1px solid ${C.divider}`,
                borderRadius: 8,
                padding: 16,
              }}
            >
              <h4
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: C.primary,
                  margin: "0 0 10px",
                  borderBottom: `1px solid ${C.divider}`,
                  paddingBottom: 6,
                }}
              >
                4. Culture & Perks
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 10,
                }}
              >
                {c4.dressCode && (
                  <div>
                    <strong>Dress Code:</strong> {c4.dressCode}
                  </div>
                )}
                {c4.cabService && (
                  <div>
                    <strong>Cab Facility:</strong> {c4.cabService}
                  </div>
                )}
                {c4.foodProvided && (
                  <div>
                    <strong>Food / Cafeteria:</strong> {c4.foodProvided}
                  </div>
                )}
                {c4.teamCulture && (
                  <div>
                    <strong>Team Culture:</strong> {c4.teamCulture}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Action Buttons */}
        <div
          style={{
            marginTop: 24,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: 6,
              border: `1px solid ${C.divider}`,
              background: "#fff",
              color: C.textDark,
              cursor: "pointer",
              fontWeight: 700,
              width: isMobile ? "100%" : "auto",
              minHeight: 44,
            }}
          >
            Close
          </button>
          {status === "SHORTLISTED" ? (
            <div
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                background: "#dcfce7",
                color: "#166534",
                fontWeight: 700,
                fontSize: 13,
                border: "1px solid #bbf7d0",
                width: isMobile ? "100%" : "auto",
                minHeight: 44,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              Shortlisted by HR
            </div>
          ) : status === "REJECTED" ? (
            <div
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                background: "#fee2e2",
                color: "#991b1b",
                fontWeight: 700,
                fontSize: 13,
                border: "1px solid #fca5a5",
                width: isMobile ? "100%" : "auto",
                minHeight: 44,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <InlineIcon name="close" className="mr-1.5" />
              Application Not Selected
            </div>
          ) : isApplied ? (
            <button
              disabled
              title="You have already applied and shared your verified profile for this opportunity"
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: `1px solid ${C.divider}`,
                background: "#e2e8f0",
                color: "#64748b",
                fontWeight: 700,
                cursor: "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                width: isMobile ? "100%" : "auto",
                minHeight: 44,
              }}
            >
              <InlineIcon name="check" />
              Profile Shared & Applied
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                onApply(job);
                onClose();
              }}
              disabled={applying}
              className="disabled:opacity-60"
              style={{
                padding: "10px 24px",
                borderRadius: 6,
                border: 0,
                background: C.primary,
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                width: isMobile ? "100%" : "auto",
                minHeight: 44,
              }}
            >
              Apply & Share Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared Modal Wrapper
// `error` is the server's answer when the save failed; `saving` while it runs.
function ModalWrapper({ title, children, onClose, onSave, saving, error }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ ...modalOverlayStyle, padding: isMobile ? 12 : 20 }}>
      <div style={{ ...modalContentStyle, padding: isMobile ? 16 : 28 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${C.divider}`,
            paddingBottom: 12,
            marginBottom: 20,
          }}
        >
          <h2
            style={{ fontSize: 18, fontWeight: 800, color: C.navy, margin: 0 }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              cursor: "pointer",
              color: C.textMed,
            }}
          >
            <InlineIcon name="close" size={20} />
          </button>
        </div>
        {children}
        {error && (
          <p
            role="alert"
            className="m-0 mt-5 rounded-lg border border-[#B24020] bg-[#FCEBE7] px-3.5 py-2.5 text-xs font-semibold text-[#B24020]"
          >
            Not saved: {error}
          </p>
        )}
        <div
          style={{
            marginTop: 24,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: 6,
              border: `1px solid ${C.divider}`,
              background: "#fff",
              color: C.textDark,
              cursor: "pointer",
              fontWeight: 700,
              width: isMobile ? "100%" : "auto",
              minHeight: 44,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave || onClose}
            disabled={saving}
            className="disabled:opacity-60"
            style={{
              padding: "10px 24px",
              borderRadius: 6,
              border: 0,
              background: C.primary,
              color: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              width: isMobile ? "100%" : "auto",
              minHeight: 44,
            }}
          >
            {saving ? "Saving..." : "Save Preferences"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shared Options
const EXP_YEARS_OPTIONS = [
  "Fresher",
  "0-1",
  "1-2",
  "2-3",
  "3-4",
  "4-5",
  "5-6",
  "6-7",
  "7-8",
  "8-9",
  "9-10",
  "10-11",
  "11-12",
  "12-13",
  "13-14",
  "14-15",
  "15-20",
  "20-25",
  "25-30",
  "30-35",
  "over 35 years",
];

const INDUSTRIES = [
  "Accounting / Auditing",
  "Advertising & Marketing",
  "Agriculture / Forestry / Fishing",
  "Analytics / KPO / Research",
  "Animation & VFX",
  "Architecture / Interior Design",
  "Auto components",
  "Automobile",
  "Aviation",
  "Banking",
  "Beverages",
  "Biotechnology",
  "BPO / Call centre",
  "Building materials",
  "Chemicals",
  "Clinical Research / Contract Research",
  "Consumer Electronics & Appliances",
  "Courier & Logistics",
  "Defence and Aerospace",
  "Design",
  "Education / Training",
  "E-Learning / Ed Tech",
  "Electrical Equipment",
  "Electronic Component / Semiconductors",
  "Electronics Manufacturing",
  "Emerging Technologies",
  "Engineering and construction",
  "Events & Live Entertainment",
  "Facility Management Services",
  "Fertilisers / Pesticides / Agrochemicals",
  "Film / Music / Entertainment",
  "Fin Tech / Payments",
  "Financial Services",
  "Fitness & Wellness",
  "FMCG",
  "Food Processing",
  "Gaming",
  "Gems & Jewellery",
  "Government / Public Administration",
  "Hardware and Networking",
  "Hotels & Restaurants",
  "Import & Export",
  "Industrial Automation",
  "Industrial Equipment / Machinery",
  "Insurance",
  "Internet",
  "Investment Banking / Venture Capital / Private Equity",
  "Iron and Steel",
  "IT Services and consulting",
  "Law Enforcement / Security",
  "Leather",
  "Legal",
  "Management consulting",
  "Medical Devices and Equipments",
  "Medical Services / Hospitals",
  "Metals and Mining",
  "NBFC",
  "NGO / Social Services / Industry Associations",
  "Oil and Gas",
  "Packaging and Containers",
  "Petrochemicals / Plastics and Rubber",
  "Pharmaceuticals / Life Sciences",
  "Ports and Shipping",
  "Power",
  "Printing and Publishing",
  "Railways",
  "Real Estate",
  "Recruitment / staffing",
  "Retail",
  "Software Product",
  "Telecom / ISP",
  "Textile & Apparel",
  "Travel & Tourism",
  "TV and Radio",
  "Water Treatment / Waste management",
  "Others",
];

const DISTRICTS = [
  "Ariyalur",
  "Chengalpattu",
  "Chennai",
  "Coimbatore",
  "Cuddalore",
  "Dharmapuri",
  "Dindigul",
  "Erode",
  "Kallakurichi",
  "Kanchipuram",
  "Kanyakumari",
  "Karur",
  "Krishnagiri",
  "Madurai",
  "Mayiladuthurai",
  "Nagapattinam",
  "Namakkal",
  "Nilgiris",
  "Perambalur",
  "Pudukkottai",
  "Ramanathapuram",
  "Ranipet",
  "Salem",
  "Sivaganga",
  "Tenkasi",
  "Thanjavur",
  "Theni",
  "Thoothukudi",
  "Tiruchirappalli",
  "Tirunelveli",
  "Tirupathur",
  "Tiruppur",
  "Tiruvallur",
  "Tiruvannamalai",
  "Tiruvarur",
  "Vellore",
  "Viluppuram",
  "Virudhunagar",
  "Bengaluru",
  "Hyderabad",
  "Mumbai",
  "Delhi NCR",
  "Others",
];

// Reusable Styles
const interactiveCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  borderRadius: 20, // Same smooth 20px rounded corners as tiles
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)", // Soft tile shadow
  transition:
    "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
};

const cardHeaderStyle = {
  fontSize: 12.5,
  fontWeight: 800,
  color: C.primaryDark,
  background: C.primaryLight,
  padding: "12px 16px",
  margin: 0,
  textTransform: "uppercase",
  letterSpacing: "0.2px",
  borderBottom: "1px solid rgba(8, 131, 62, 0.12)",
};

const cardBodyStyle = {
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  flex: 1,
};
const cardSummaryTextStyle = {
  fontSize: 12.5,
  color: C.textMed,
  lineHeight: 1.6,
};
const cardLinkTextStyle = {
  marginTop: "auto",
  paddingTop: 8,
  fontSize: 11.5,
  fontWeight: 700,
  color: C.primary,
};
const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: C.textDark,
};
const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: `1px solid ${C.divider}`,
  fontSize: 13,
  boxSizing: "border-box",
  fontFamily: "inherit",
};
const textareaStyle = { ...inputStyle, resize: "vertical" };
const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(3px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 20,
};
const modalContentStyle = {
  background: "#fff",
  borderRadius: 12,
  width: "100%",
  maxWidth: 820,
  maxHeight: "90vh",
  overflowY: "auto",
  padding: 28,
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
};
